import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ArrowRight, Gift } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useCart } from "../lib/cart";
import { formatINR } from "../lib/api";
import { Button } from "../components/ui/button";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal, hamperLinePrice } = useCart();
  const nav = useNavigate();

  return (
    <div className="App">
      <Navbar />
      <div className="mx-auto max-w-7xl px-6 py-14 lg:px-10 lg:py-20" data-testid="cart-page">
        <p className="label-tiny mb-3">Your basket</p>
        <h1 className="font-serif text-4xl text-[#2a2928] sm:text-5xl">Cart</h1>

        {items.length === 0 ? (
          <div className="mt-10 rounded-3xl border border-dashed border-[#e8e2da] py-20 text-center" data-testid="cart-empty">
            <p className="text-[#7a7571]">Your cart is empty.</p>
            <Link
              to="/shop"
              data-testid="cart-continue-shopping"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#d08c8c] px-6 py-3 text-sm font-medium text-white hover:bg-[#b87a7a]"
            >
              Continue shopping <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        ) : (
          <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-3">
            <div className="space-y-4 lg:col-span-2" data-testid="cart-items-list">
              {items.map((it) => {
                const perHamper = hamperLinePrice(it);
                const hasAddons = it.addons && it.addons.length > 0;
                return (
                  <div
                    key={it.line_id}
                    data-testid={`cart-item-${it.line_id}`}
                    className="rounded-3xl border border-[#e8e2da] bg-white p-4"
                  >
                    <div className="flex items-start gap-4">
                      <img
                        src={it.image_url}
                        alt={it.name}
                        className="h-24 w-24 flex-shrink-0 rounded-2xl object-cover"
                      />
                      <div className="flex-1">
                        <h3 className="font-serif text-lg text-[#2a2928]">{it.name}</h3>
                        <div className="mt-1 text-sm text-[#7a7571]">Hamper base · {formatINR(it.price)}</div>
                        <div className="mt-3 flex items-center gap-2">
                          <div className="flex items-center gap-2 rounded-full border border-[#e8e2da] px-2 py-1">
                            <button
                              data-testid={`cart-qty-decrease-${it.line_id}`}
                              onClick={() => updateQty(it.line_id, it.quantity - 1)}
                              className="rounded-full p-1 hover:bg-[#f4ede6]"
                            >
                              <Minus className="h-3.5 w-3.5" />
                            </button>
                            <span className="min-w-[20px] text-center text-sm">{it.quantity}</span>
                            <button
                              data-testid={`cart-qty-increase-${it.line_id}`}
                              onClick={() => updateQty(it.line_id, it.quantity + 1)}
                              className="rounded-full p-1 hover:bg-[#f4ede6]"
                            >
                              <Plus className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          <button
                            data-testid={`cart-remove-${it.line_id}`}
                            onClick={() => removeItem(it.line_id)}
                            className="ml-2 inline-flex items-center gap-1 text-sm text-[#7a7571] hover:text-[#d08c8c]"
                          >
                            <Trash2 className="h-3.5 w-3.5" /> Remove
                          </button>
                        </div>
                      </div>
                      <div className="whitespace-nowrap text-right">
                        <div className="font-serif text-lg text-[#2a2928]">
                          {formatINR(perHamper * it.quantity)}
                        </div>
                        {it.quantity > 1 && (
                          <div className="text-xs text-[#7a7571]">{formatINR(perHamper)} each</div>
                        )}
                      </div>
                    </div>

                    {hasAddons && (
                      <div className="mt-4 rounded-2xl bg-[#faf9f6] p-3" data-testid={`cart-addons-${it.line_id}`}>
                        <div className="mb-2 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#7a7571]">
                          <Gift className="h-3 w-3" /> Gifts in the box
                        </div>
                        <div className="space-y-1.5 text-sm">
                          {it.addons.map((a) => (
                            <div key={a.addon_id} className="flex justify-between">
                              <span className="text-[#2a2928]">{a.name} × {a.quantity}</span>
                              <span className="text-[#7a7571]">{formatINR(a.price * a.quantity)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {(it.selected_toys && it.selected_toys.length > 0) && (
                      <div className="mt-3 rounded-2xl bg-[#faf9f6] p-3" data-testid={`cart-toys-${it.line_id}`}>
                        <div className="mb-2 text-xs uppercase tracking-[0.18em] text-[#7a7571]">
                          Toys inside the balloon
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {it.selected_toys.map((t) => (
                            <span key={t.toy_id} className="rounded-full bg-white px-2 py-0.5 text-xs text-[#2a2928] border border-[#e8e2da]">
                              {t.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="h-fit rounded-3xl border border-[#e8e2da] bg-white p-8" data-testid="cart-summary">
              <h3 className="font-serif text-xl text-[#2a2928]">Order summary</h3>
              <div className="mt-6 space-y-3 text-sm text-[#2a2928]">
                <div className="flex justify-between">
                  <span className="text-[#7a7571]">Subtotal</span>
                  <span data-testid="cart-subtotal">{formatINR(subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#7a7571]">Delivery</span>
                  <span>Calculated at checkout</span>
                </div>
                <div className="mt-4 flex justify-between border-t border-[#e8e2da] pt-4 font-serif text-lg">
                  <span>Total</span>
                  <span data-testid="cart-total">{formatINR(subtotal)}</span>
                </div>
              </div>
              <Button
                data-testid="cart-checkout-btn"
                onClick={() => nav("/checkout")}
                className="mt-8 w-full rounded-full bg-[#d08c8c] py-6 text-white hover:bg-[#b87a7a]"
              >
                Proceed to checkout
              </Button>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
